import { useTranslation } from 'react-i18next';
import { Check, Mountain } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import type { CompetitionCategory } from '../../../types/competition';
import { RoundClimbThumbnail } from '../RoundClimbThumbnail';
import { blockOpenToMe, type WallBlock } from './judge-blocks';

interface WallBlocksListProps {
  blocks: WallBlock[];
  /** The round's classes, to name the ones that climb each block. */
  categories: CompetitionCategory[];
  onSelect: (block: WallBlock) => void;
  /** The block on screen, when the list is the drawer of a block's page. */
  currentKey?: string;
}

/**
 * The round as a judge reads it: one row per block **on the wall**, whatever
 * the number of classes that climb it (see `wallBlocks`), with the classes named
 * under it. Tapping a row is how a judge gets to the block.
 *
 * A block whose every class was handed to another judge is listed, named after
 * its judges, and does not open: a judge at the wall wants the whole round in
 * front of them, and on a block that is not theirs, whose it is.
 */
export function WallBlocksList({ blocks, categories, onSelect, currentKey }: WallBlocksListProps) {
  const { t } = useTranslation();

  if (blocks.length === 0) {
    return <p className="py-8 text-center text-muted-foreground">{t('competitionRounds.noClimbs')}</p>;
  }

  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name;

  return (
    <ul className="divide-y" data-testid="wall-blocks">
      {blocks.map((block) => {
        const mine = blockOpenToMe(block);
        const current = block.key === currentKey;
        const withPhoto = block.roundClimbs.find((rc) => rc.image_small_url) ?? block.roundClimbs[0];
        const classNames = block.roundClimbs.some((rc) => rc.competition_category_id === null)
          ? [t('judgeBlock.allClasses')]
          : block.roundClimbs.flatMap((rc) => {
              const name = rc.competition_category_id ? categoryName(rc.competition_category_id) : null;
              return name ? [name] : [];
            });
        const judges = [
          ...new Set(
            block.roundClimbs.flatMap((rc) => (rc.requires_judge ? (rc.judges ?? []).map((j) => j.name) : []))
          ),
        ];

        return (
          <li
            key={block.key}
            className={cn('flex items-center gap-3 px-1 py-2', current && 'bg-muted')}
            data-testid="wall-block-row"
            data-state={mine ? undefined : 'not-mine'}
          >
            {/* Beside the row's button rather than in it: the photo is a button
                of its own, which enlarges it. */}
            {withPhoto.image_small_url ? (
              <RoundClimbThumbnail roundClimb={withPhoto} className="h-12 w-12" />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
                <Mountain className="h-6 w-6 text-muted-foreground" aria-hidden />
              </span>
            )}
            <button
              type="button"
              onClick={() => mine && onSelect(block)}
              disabled={!mine}
              aria-current={current || undefined}
              className={cn(
                'flex min-w-0 flex-1 items-center gap-3 self-stretch text-left',
                mine ? 'hover:opacity-80' : 'cursor-default text-muted-foreground'
              )}
              data-testid="wall-block-button"
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{block.name}</span>
                  {block.roundClimbs.some((rc) => rc.requires_judge) && (
                    <Badge variant="outline" className="border-blue-600 text-xs text-blue-600">
                      {t('competitionRounds.requiresJudge')}
                    </Badge>
                  )}
                </span>
                {classNames.length > 0 && (
                  <span className="block truncate text-xs text-muted-foreground" data-testid="wall-block-classes">
                    {classNames.join(' · ')}
                  </span>
                )}
                {/* Who stands at this block. On somebody else's it is the
                    answer to "why can't I open it". */}
                {judges.length > 0 && (
                  <span className="block text-xs text-muted-foreground" data-testid="block-judges">
                    {t('competitionRounds.assignedJudges', { count: judges.length, names: judges.join(', ') })}
                  </span>
                )}
              </span>
              {current && <Check className="size-4 shrink-0" aria-hidden />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
